/*
  Solver-Läufe als geteilter Zustand von Toolbar (Badge, Rechen-Ring) und
  SolverPanel (Lauf-Liste). Pollt im Hintergrund, solange die Seite offen ist:
  ruhig alle 30 s, alle 4 s sobald ein Lauf rechnet oder der Dialog offen ist.
  Nur für Admins aktiv (enabled) — das Backend filtert die Läufe zusätzlich
  nach den sichtbaren Standorten des Nutzers.
*/

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listSolverRuns } from "../../api/solverApi";

const IDLE_POLL_MS = 30000;
const ACTIVE_POLL_MS = 4000;

export const SOLVER_ACTIVE_STATUSES = new Set(["queued", "running"]);

export default function useSolverRuns({ enabled = false, fast = false } = {}) {
  const [runs, setRuns] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [currentLocationId, setCurrentLocationId] = useState(null);
  // null = keine Aussage (kein Super_Admin oder nicht feststellbar).
  const [workerAlive, setWorkerAlive] = useState(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const refresh = useCallback(async () => {
    if (!enabledRef.current) return null;
    try {
      const data = await listSolverRuns();
      setRuns(data.runs);
      setAvailableLocations(data.availableLocations);
      setCurrentLocationId(data.currentLocationId);
      setWorkerAlive(data.workerAlive ?? null);
      return data.runs;
    } catch (error) {
      // Stilles Weiterpollen — der Dialog meldet Fehler bei seinen Aktionen selbst.
      return null;
    }
  }, []);

  const hasActiveRun = useMemo(() => runs.some((run) => SOLVER_ACTIVE_STATUSES.has(run.status)), [runs]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const interval = fast || hasActiveRun ? ACTIVE_POLL_MS : IDLE_POLL_MS;
    const timer = window.setInterval(refresh, interval);
    return () => window.clearInterval(timer);
  }, [enabled, fast, hasActiveRun, refresh]);

  // Badge = fertige Läufe, die auf Übernehmen/Verwerfen warten (Grill-Entscheid).
  const pendingResultCount = useMemo(() => runs.filter((run) => run.status === "done").length, [runs]);

  return {
    runs,
    availableLocations,
    currentLocationId,
    workerAlive,
    hasActiveRun,
    pendingResultCount,
    refresh,
  };
}
